import { mapToUser } from 'api/mappers/portalAdmin'
import { mapToTeamMembers } from 'api/mappers/teamMember'
import {
  CreateWorkspaceAdminRequest,
  DeleteWorkspaceAdminRequest,
  GetOrganizationAdminRequest,
  GetWorkspaceAdminInviteDetailsRequest,
  GetWorkspaceAdminRequest,
  GetWorkspaceAdminsRequest,
  UpdateWorkspaceAdminRequest,
} from 'lib/generated/api'
import { useMutation, useQuery, useQueryClient } from 'react-query'
import { useApiClient } from 'contexts/ApiClientContext'
import { organizationWorkspaceUserProfilesKey } from './organizations'

const queryKeys = {
  organizationAdmin: 'getOrganizationAdmin',
  workspaceAdmin: 'getWorkspaceAdmin',
  workspaceAdmins: 'getWorkspaceAdmins',
  workspaceAdminInviteDetails: 'getWorkspaceAdminInviteDetails',
}

/* Organization Admin */

export const organizationAdminQueryKey = (
  request: GetOrganizationAdminRequest
) => [
  queryKeys.organizationAdmin,
  request.organizationId,
  request.portalAdminId,
]

export const useGetOrganizationAdmin = (
  request: GetOrganizationAdminRequest
) => {
  const { portalAdminApi } = useApiClient()

  return useQuery(organizationAdminQueryKey(request), () =>
    portalAdminApi.getOrganizationAdmin(request)
  )
}

/* Workspace Admin */

export const workspaceAdminQueryKey = (request: GetWorkspaceAdminRequest) => [
  queryKeys.workspaceAdmin,
  request.workspaceId,
  request.portalAdminId,
]

export const workspaceAdminsQueryKey = ({
  workspaceId,
  ...queryParams
}: GetWorkspaceAdminsRequest) => [
  queryKeys.workspaceAdmins,
  workspaceId,
  queryParams,
]

export const workspaceAdminInviteDetailsQueryKey = (
  request: GetWorkspaceAdminInviteDetailsRequest
) => [queryKeys.workspaceAdminInviteDetails, request.invitationId]

export const useGetWorkspaceAdmin = (request: GetWorkspaceAdminRequest) => {
  const { portalAdminApi } = useApiClient()
  return useQuery(
    workspaceAdminQueryKey(request),
    () => portalAdminApi.getWorkspaceAdmin(request),
    {
      enabled:
        !!request.portalAdminId &&
        !!request.workspaceId &&
        !!request.organizationId,
      select: mapToUser,
    }
  )
}

export const useGetWorkspaceAdmins = (request: GetWorkspaceAdminsRequest) => {
  const { portalAdminApi } = useApiClient()

  return useQuery(workspaceAdminsQueryKey(request), () =>
    portalAdminApi.getWorkspaceAdmins(request)
  )
}

export const useTeamMembersQuery = (request: GetWorkspaceAdminsRequest) => {
  const { portalAdminApi } = useApiClient()

  return useQuery(
    workspaceAdminsQueryKey(request),
    () => portalAdminApi.getWorkspaceAdmins(request),
    { select: mapToTeamMembers }
  )
}

export const useUpdateWorkspaceAdmin = () => {
  const queryClient = useQueryClient()
  const { portalAdminApi } = useApiClient()

  return useMutation(
    (request: UpdateWorkspaceAdminRequest) =>
      portalAdminApi.updateWorkspaceAdmin(request),
    {
      onSuccess: (data, variables) => {
        queryClient.setQueryData(workspaceAdminQueryKey(variables), data)
        queryClient.invalidateQueries(organizationWorkspaceUserProfilesKey)
      },
    }
  )
}

export const useDeleteWorkspaceAdmin = () => {
  const queryClient = useQueryClient()
  const { portalAdminApi } = useApiClient()

  return useMutation(
    (request: DeleteWorkspaceAdminRequest) =>
      portalAdminApi.deleteWorkspaceAdmin(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([
          queryKeys.workspaceAdmins,
          variables.workspaceId,
        ])
      },
    }
  )
}

export const useCreateWorkspaceAdmin = () => {
  const queryClient = useQueryClient()
  const { portalAdminApi } = useApiClient()

  return useMutation(
    (request: CreateWorkspaceAdminRequest) =>
      portalAdminApi.createWorkspaceAdmin(request),
    {
      onSuccess: (data, variables) => {
        queryClient.invalidateQueries([
          queryKeys.workspaceAdmins,
          variables.workspaceId,
        ])
      },
    }
  )
}

export const useGetWorkspaceAdminInviteDetails = (
  request: GetWorkspaceAdminInviteDetailsRequest
) => {
  const { portalAdminApi } = useApiClient()

  return useQuery(
    workspaceAdminInviteDetailsQueryKey(request),
    () => portalAdminApi.getWorkspaceAdminInviteDetails(request),
    { enabled: !!request.invitationId }
  )
}
